import React, { useState } from 'react';

import { ENERGY_TAGS } from '@/config/constants';
import { useUpdateTeamSongMutation } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { LimitedTeamSong, useTeamSongs } from '@/providers/TeamSongsProvider';
import { Popover } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';

import { SongThemeChip } from '../shared/SongThemeChip';
import styles from './TeamSongTags.module.scss';

type TeamSongTagsProps = {
	className?: string;
	chipClassName?: string;
	teamSongIds: string[];
	renderValue?: (currentSong?: LimitedTeamSong) => string;
	editable?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const TeamSongTags: React.FC<TeamSongTagsProps> = ({
	teamSongIds,
	className,
	chipClassName,
	renderValue,
	editable = false,
	...divProps
}) => {
	const { findTeamSongsById } = useTeamSongs();
	const classes = classNames([styles['root'], className]);
	const setlistTeamSongs = findTeamSongsById(teamSongIds);

	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [editingSong, setEditingSong] = useState<LimitedTeamSong | null>(null);
	const { mutate: updateTeamSong } = useUpdateTeamSongMutation();
	const queryClient = useQueryClient();

	const handleClose = () => {
		setAnchorEl(null);
		setEditingSong(null);
	};

	const handleEnergySelect = (tag: string) => {
		if (!editingSong) return;
		updateTeamSong(
			{
				teamSongId: editingSong.id,
				input: {
					name: editingSong.name,
					artist: editingSong.artist,
					energyTags: [tag],
				},
			},
			{
				onSettled: () => {
					queryClient.invalidateQueries({ queryKey: ['ListTeamSongs'] });
					handleClose();
				},
			},
		);
	};

	return (
		<>
			<div className={classes} {...divProps}>
				{setlistTeamSongs.map((currentSong) => {
					const tag = renderValue
						? renderValue(currentSong)
						: currentSong?.energyTags?.[0];

					if (!tag && editable && currentSong) {
						return (
							<SongThemeChip
								key={`chip-add-${currentSong.id}`}
								className={classNames([chipClassName, styles['tag'], styles['add-chip']])}
								themeTag="Add energy"
								onClick={(e) => {
									setAnchorEl(e.currentTarget);
									setEditingSong(currentSong);
								}}
							/>
						);
					}

					return (
						<SongThemeChip
							key={`chip-${currentSong?.id}`}
							className={classNames([chipClassName, styles['tag']])}
							themeTag={tag || '?'}
						/>
					);
				})}
			</div>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				transformOrigin={{ vertical: 'top', horizontal: 'center' }}
			>
				<div className={styles['energy-picker']}>
					{ENERGY_TAGS.map((energyTag) => (
						<SongThemeChip
							key={energyTag.label}
							className={styles['picker-chip']}
							themeTag={energyTag.capitalisedLabel}
							onClick={() => handleEnergySelect(energyTag.label)}
						/>
					))}
				</div>
			</Popover>
		</>
	);
};
